import styled from '@emotion/styled';
import { Fragment } from 'react';
import { colors, fonts, radii } from './tokens';
import { CheckIcon } from './icons';

// Figma stepper: numbered pills joined by dotted connectors ("1 Calibration ··· 2 Distance ··· 3 Examinations").
// active step: dark-teal filled number; done: green circle with a check; upcoming: grey outline.

const Row = styled.ol`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
  font-family: ${fonts.family};
`;

const Pill = styled.li`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px 4px 4px;
  border-radius: ${radii.pill}px;
  background: ${({ state }) => (state === 'upcoming' ? colors.slate[50] : colors.lightTeal)};
  color: ${({ state }) => (state === 'active' ? colors.darkTeal : state === 'done' ? colors.primary : colors.slate[500])};
  font-size: 13px;
  font-weight: ${({ state }) => (state === 'active' ? 700 : 500)};
  white-space: nowrap;
`;

const Num = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
  box-sizing: border-box;
  ${({ state }) =>
    state === 'active'
      ? `background:${colors.darkTeal};color:#fff;`
      : state === 'done'
        ? `background:${colors.success[500]};color:#fff;`
        : `border:1px solid ${colors.slate[300]};color:${colors.slate[500]};background:#fff;`}
`;

const Connector = styled.span`
  width: 28px;
  border-top: 2px dotted ${({ done }) => (done ? colors.success[500] : colors.slate[300])};
  @media (max-width: 420px) {
    width: 12px;
  }
`;

/**
 * Stepper
 * @param steps   array of labels (default Calibration / Distance / Examinations)
 * @param current 0-based index of the active step; steps before it render as done
 * @param done    optional array of indices that are done regardless of `current`
 */
export const Stepper = ({ steps = ['Calibration', 'Distance', 'Examinations'], current = 0, done = [] }) => (
  <Row aria-label='Progress'>
    {steps.map((label, i) => {
      const state = done.includes(i) || i < current ? 'done' : i === current ? 'active' : 'upcoming';
      return (
        <Fragment key={label}>
          {i > 0 && <Connector aria-hidden='true' done={state === 'done' || state === 'active'} />}
          <Pill state={state} aria-current={state === 'active' ? 'step' : undefined}>
            <Num state={state}>{state === 'done' ? <CheckIcon size={13} strokeWidth={3} /> : i + 1}</Num>
            {label}
          </Pill>
        </Fragment>
      );
    })}
  </Row>
);

export default Stepper;
